import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../css/Lp2App.css";
import { bootloopClient, BootCondition, ShoeType } from "../api/bootloopClient";

type CreateListingResponse = {
  listingId?: number;
  ListingId?: number;
};

function pickId(x: any): number | null {
  const a = x?.listingId ?? x?.ListingId;
  const n = Number(a);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const ListBoot: React.FC = () => {
  const nav = useNavigate();

  const [email, setEmail] = useState(() => localStorage.getItem("bootloop.email") ?? "");
  const [postcode, setPostcode] = useState(() => localStorage.getItem("bootloop.postcode") ?? "");

  const [sizeUk, setSizeUk] = useState<number>(3.5);
  const [shoeType, setShoeType] = useState<ShoeType>(1);
  const [condition, setCondition] = useState<BootCondition>(2);
  const [notes, setNotes] = useState<string>("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);

    if (!email.trim()) {
      setErr("Please enter your email.");
      return;
    }

    try {
      setBusy(true);

      localStorage.setItem("bootloop.email", email.trim());
      if (postcode.trim()) localStorage.setItem("bootloop.postcode", postcode.trim());

      const res = (await bootloopClient.createListing({
        email: email.trim(),
        postcode: postcode.trim() || undefined,
        sizeUk,
        shoeType,
        condition,
        notes: notes.trim() || undefined,
        photoUrl: undefined,
      })) as unknown as CreateListingResponse;

      const id = pickId(res);
      if (!id) {
        nav("/boots");
        return;
      }

      // ✅ After-glow trigger
      nav(`/boots?new=${id}`);
    } catch (e: any) {
      setErr(e?.message || "Failed to create listing.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lp2app">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h1 className="lp2app__title">List boots</h1>
          <p className="lp2app__lead">Create a listing in seconds.</p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Link className="lp2app__btnSecondary" to="/boots">
            Boots available
          </Link>
          <Link className="lp2app__btnSecondary" to="/my">
            My stuff
          </Link>
        </div>
      </div>

      {err && <div className="lp2app__alert">{err}</div>}

      <div className="lp2app__card lp2app__form">
        <div className="lp2app__row2">
          <label className="lp2app__label">
            Email
            <input
              className="lp2app__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
            />
          </label>

          <label className="lp2app__label">
            Postcode (optional)
            <input
              className="lp2app__input"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. IP9 2FS"
            />
          </label>
        </div>

        <div className="lp2app__row3">
          <label className="lp2app__label">
            Size (UK)
            <input
              className="lp2app__input"
              type="number"
              step="0.5"
              min={1}
              max={15}
              value={sizeUk}
              onChange={(e) => setSizeUk(Number(e.target.value))}
            />
          </label>

          <label className="lp2app__label">
            Shoe type
            <select
              className="lp2app__select"
              value={String(shoeType)}
              onChange={(e) => setShoeType(Number(e.target.value) as ShoeType)}
            >
              <option value="0">FG (Firm Ground)</option>
              <option value="1">AG (Artificial Grass)</option>
              <option value="2">SG (Soft Ground)</option>
              <option value="3">TF (Turf / Astro)</option>
              <option value="4">IC (Indoor)</option>
            </select>
          </label>

          <label className="lp2app__label">
            Condition
            <select
              className="lp2app__select"
              value={String(condition)}
              onChange={(e) => setCondition(Number(e.target.value) as BootCondition)}
            >
              <option value="0">New</option>
              <option value="1">Like new</option>
              <option value="2">Good</option>
              <option value="3">Fair</option>
              <option value="4">Well used</option>
            </select>
          </label>
        </div>

        <div className="lp2app__row1">
          <label className="lp2app__label" style={{ width: "100%" }}>
            Notes (optional)
            <textarea
              className="lp2app__input"
              style={{ minHeight: 90, resize: "vertical" }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Brand/model, any wear, preferred swap size…"
            />
          </label>
        </div>

        <div className="lp2app__row2">
          <div className="lp2app__smallMuted">
            After creating, you can upload multiple photos on the listing page.
          </div>

          <div className="lp2app__actions">
            <button className="lp2app__btnPrimary" onClick={submit} disabled={busy}>
              {busy ? "Creating…" : "Create listing"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListBoot;